// The provenance graph (GET /api/datasets/{id}/provenance) as a Mermaid flowchart.
//
// Pure: graph in, Mermaid text out (as pages/dbdesign/erDiagram.js does for the ER diagram).
//   datasets  -> nodes d<id>, labelled "name · author" (the author of the dataset's study,
//                created_by); the graph's own dataset gets class `root`, a dataset the user may
//                not edit (can_edit false) class `readonly`;
//   analyses  -> edges d<from> --> d<to>, one per (input, result) pair, labelled with the
//                instrument name + version (or the analysis name when it has no instrument);
//   protocol files -> nodes p<file id> (hexagons), joined to the dataset that holds them by a
//                dotted line (a protocol is a file with the role protocol since 2026-10-20)
//                labelled with the linked version.
//
// Every label is user text. Mermaid would read `"`, `]`, `|`, `<`, `#`, `;` ... as syntax or HTML,
// so escapeLabel() turns every character that is not a letter, a digit, a space or `.,_-` into a
// Mermaid entity code (#34; for `"`), and labels are always quoted. Ids are built from numbers
// only. No raw HTML ever reaches the diagram.

export function escapeLabel(text) {
  return Array.from(String(text ?? "")).map(ch => (
    /[\p{L}\p{N} .,_-]/u.test(ch) ? ch : `#${ch.codePointAt(0)};`
  )).join("");
}

export function nodeId(datasetId) {
  return `d${Number(datasetId)}`;
}

export function analysisLabel(a) {
  if (!a) return "";
  if (a.instrument_name) return [a.instrument_name, a.instrument_version].filter(Boolean).join(" ");
  return a.name || "";
}

export function buildProvenanceDiagram(graph) {
  const lines = ["flowchart LR"];
  if (!graph || !(graph.nodes || []).length) return lines.join("\n");
  const analyses = Object.fromEntries((graph.analyses || []).map(a => [a.id, a]));

  (graph.nodes || []).forEach(n => {
    const author = n.created_by ? (n.created_by.full_name || n.created_by.username) : "";
    const label = escapeLabel(author ? `${n.name} · ${author}` : n.name);
    const shape = n.kind === "derived" ? [`(["`, `"])`] : [`["`, `"]`];
    lines.push(`    ${nodeId(n.id)}${shape[0]}${label}${shape[1]}`);
  });

  (graph.edges || []).forEach(e => {
    const label = escapeLabel(analysisLabel(analyses[e.analysis_id]));
    lines.push(`    ${nodeId(e.from_id)} -->|"${label}"| ${nodeId(e.to_id)}`);
  });

  const protocols = new Map();
  (graph.nodes || []).forEach(n => (n.protocols || []).forEach(p => {
    if (!protocols.has(p.data_file_id)) protocols.set(p.data_file_id, p.filename);
  }));
  protocols.forEach((name, id) => {
    lines.push(`    p${Number(id)}{{"${escapeLabel(`Protocol: ${name}`)}"}}`);
  });
  (graph.nodes || []).forEach(n => (n.protocols || []).forEach(p => {
    lines.push(`    p${Number(p.data_file_id)} -.- ${nodeId(n.id)}`);
  }));

  lines.push("    classDef root stroke-width:3px");
  lines.push("    classDef readonly stroke-dasharray:4 3");
  lines.push("    classDef protocol fill:#f3f4f6,stroke:#6b7280");
  (graph.nodes || []).forEach(n => {
    if (n.root) lines.push(`    class ${nodeId(n.id)} root`);
    else if (!n.can_edit) lines.push(`    class ${nodeId(n.id)} readonly`);
  });
  if (protocols.size) lines.push(`    class ${[...protocols.keys()].map(id => `p${Number(id)}`).join(",")} protocol`);
  return lines.join("\n");
}

// The dataset id a rendered Mermaid node element stands for, or null. Mermaid 11.16 renders a
// flowchart node as <g class="node" id="<render id>-flowchart-d5-<n>"> (checked in Chromium,
// 2026-10-05); a `data-id="d5"` attribute, which other Mermaid versions set, is read first.
export function datasetIdOfElement(el) {
  const dataId = el.getAttribute && el.getAttribute("data-id");
  let m = dataId && dataId.match(/^d(\d+)$/);
  if (!m) m = (el.id || "").match(/flowchart-d(\d+)-\d+$/);
  return m ? Number(m[1]) : null;
}
